// Ported from the proto-assets-filters prototype branch
// (apps/customer/src/routes/assets/_prototype/useModalTrap.ts), which fixed
// a real P0 found by Impeccable review: the mobile filter sheet declared
// role="dialog" / aria-modal="true" and implemented none of the behavior
// those attributes promise (focus never moved into the panel, nothing
// trapped Tab — 27 background controls stayed reachable — Escape dropped
// focus to <body>, body scroll was never locked).

import { useEffect, useRef, type RefObject } from 'react';

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

function isVisible(el: HTMLElement): boolean {
  return el.offsetParent !== null || el === document.activeElement;
}

function getFocusable(container: HTMLElement | null): HTMLElement[] {
  if (!container) return [];
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(isVisible);
}

/**
 * The trigger the panel opened from, if it's still a legitimate restore
 * target — connected to the DOM and visible. If it isn't (the row it lived
 * in was removed, a re-render swapped it out, whatever), fall back to a
 * stable element rather than leaving focus on `<body>`: `#root` itself,
 * given a `tabindex="-1"` the first time it's needed so it's a valid
 * programmatic focus target without joining the Tab order.
 */
function resolveRestoreTarget(candidate: HTMLElement | null): HTMLElement | null {
  if (candidate && candidate.isConnected && isVisible(candidate)) return candidate;
  const root = document.getElementById('root');
  if (root) {
    if (!root.hasAttribute('tabindex')) root.setAttribute('tabindex', '-1');
    return root;
  }
  return null;
}

/**
 * Wire a panel element up to the full modal contract: focus moves into it
 * on open, Tab/Shift+Tab wrap inside it while open, the app root goes
 * `inert` so nothing behind it is reachable or announced, body scroll
 * locks, and focus returns to whatever triggered the open on close.
 *
 * Every close path (Escape, Cancel, Apply, backdrop click) already routes
 * through the same `open` -> `false` transition in the caller, so all four
 * get restore-on-close for free from one effect cleanup — no need to
 * special-case any of them here.
 *
 * Caller is expected to render the panel via `createPortal(…,
 * document.body)` so marking `#root` inert doesn't also swallow the panel
 * itself (the panel lives outside `#root` once portaled).
 */
export function useModalTrap(open: boolean): RefObject<HTMLDivElement> {
  const panelRef = useRef<HTMLDivElement>(null);
  const restoreFocusTo = useRef<HTMLElement | null>(null);

  // Focus-in, inert, and scroll-lock all live in ONE effect so close-time
  // ordering is explicit rather than dependent on React's multi-effect
  // cleanup order. Splitting this into two effects (focus-restore,
  // inert+scroll-lock) means their cleanups run in declaration order —
  // restore first, un-inert second — so the restore's `.focus()` call
  // lands while `#root` is STILL inert and silently no-ops (an element
  // inside, or whose focus delegate is inside, an inert subtree cannot
  // take focus). Un-inert and unlock run BEFORE the focus() call below.
  useEffect(() => {
    if (!open) return;

    restoreFocusTo.current = document.activeElement as HTMLElement | null;

    const root = document.getElementById('root');
    const prevBodyOverflow = document.body.style.overflow;
    const prevHtmlOverflow = document.documentElement.style.overflow;
    root?.setAttribute('inert', '');
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';

    const focusable = getFocusable(panelRef.current);
    (focusable[0] ?? panelRef.current)?.focus();

    return () => {
      // Order matters: the background must be focusable again BEFORE we
      // try to focus something in it.
      root?.removeAttribute('inert');
      document.body.style.overflow = prevBodyOverflow;
      document.documentElement.style.overflow = prevHtmlOverflow;

      const target = resolveRestoreTarget(restoreFocusTo.current);
      target?.focus();
      // Belt-and-suspenders: some browsers haven't finished re-evaluating
      // focusability in the same synchronous tick the `inert` attribute
      // is removed. One rAF is enough to land after the next style/layout
      // pass; only retries if the first attempt didn't take.
      requestAnimationFrame(() => {
        if (target && document.activeElement !== target) target.focus();
      });
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const focusable = getFocusable(panelRef.current);
      if (focusable.length === 0) {
        e.preventDefault();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last?.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first?.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => { document.removeEventListener('keydown', onKeyDown); };
  }, [open]);

  return panelRef;
}
